import { queryOptions } from '@tanstack/react-query';

import type { Tables, Views } from '@archetelos/db';

import { supabase } from '@/lib/supabase';

export type SubscriberStats = Views<'subscriber_stats'>;
export type Bookmark = Tables<'bookmarks'>;
export type ReadingProgress = Tables<'reading_progress'>;
export type WatchProgress = Tables<'watch_progress'>;
export type Profile = Tables<'profiles'>;

export interface RecommendationRow {
  content_id: string;
  type: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  cover_image_alt: string | null;
  tradition_tags: string[];
  topic_tags: string[];
  creator_id: string;
  published_at: string | null;
  score?: number;
}

export interface FeedRow extends RecommendationRow {
  creator_display_name: string | null;
  creator_username: string | null;
}

export const subscriberQueries = {
  stats: () =>
    queryOptions({
      queryKey: ['subscriber-stats'] as const,
      queryFn: async (): Promise<SubscriberStats | null> => {
        const { data, error } = await supabase.rpc('get_subscriber_stats');
        if (error) throw error;
        return (data?.[0] ?? null) as SubscriberStats | null;
      },
    }),

  latestPublished: () =>
    queryOptions({
      queryKey: ['latest-published'] as const,
      queryFn: async (): Promise<RecommendationRow[]> => {
        const { data, error } = await supabase
          .from('content')
          .select(
            'id, type, title, slug, excerpt, cover_image_url, cover_image_alt, tradition_tags, topic_tags, creator_id, published_at',
          )
          .eq('status', 'published')
          .order('published_at', { ascending: false })
          .limit(18);
        if (error) throw error;
        return (data ?? []).map((c) => ({
          ...(c as Record<string, unknown>),
          content_id: (c as { id: string }).id,
        })) as RecommendationRow[];
      },
    }),

  recommendations: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['recommendations', userId] as const,
      queryFn: async (): Promise<RecommendationRow[]> => {
        if (!userId) return [];
        const { data, error } = await supabase.rpc('recommend_for_user', {
          user_uuid: userId,
          result_limit: 18,
        });
        if (error) throw error;
        return (data ?? []) as RecommendationRow[];
      },
      enabled: Boolean(userId),
    }),

  feed: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['followed-feed', userId] as const,
      queryFn: async (): Promise<FeedRow[]> => {
        if (!userId) return [];
        const { data, error } = await supabase.rpc('followed_feed', {
          user_uuid: userId,
          result_limit: 30,
          result_offset: 0,
        });
        if (error) throw error;
        return (data ?? []) as FeedRow[];
      },
      enabled: Boolean(userId),
    }),

  following: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['following', userId] as const,
      queryFn: async (): Promise<string[]> => {
        if (!userId) return [];
        const { data, error } = await supabase
          .from('follows')
          .select('followed_id')
          .eq('follower_id', userId);
        if (error) throw error;
        return (data ?? []).map((r) => r.followed_id);
      },
      enabled: Boolean(userId),
    }),

  creators: () =>
    queryOptions({
      queryKey: ['creators'] as const,
      queryFn: async (): Promise<Profile[]> => {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('role', 'creator')
          .eq('creator_status', 'approved')
          .order('display_name', { ascending: true });
        if (error) throw error;
        return (data ?? []) as Profile[];
      },
    }),

  creatorByUsername: (username: string | undefined) =>
    queryOptions({
      queryKey: ['creator-profile', username] as const,
      queryFn: async (): Promise<Profile | null> => {
        if (!username) return null;
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('username', username)
          .maybeSingle();
        if (error) throw error;
        return data as Profile | null;
      },
      enabled: Boolean(username),
    }),

  creatorContent: (creatorId: string | undefined) =>
    queryOptions({
      queryKey: ['creator-content', creatorId] as const,
      queryFn: async () => {
        if (!creatorId) return [];
        const { data, error } = await supabase
          .from('content')
          .select(
            'id, type, title, slug, excerpt, cover_image_url, cover_image_alt, tradition_tags, topic_tags, creator_id, published_at',
          )
          .eq('creator_id', creatorId)
          .eq('status', 'published')
          .order('published_at', { ascending: false });
        if (error) throw error;
        return (data ?? []).map((c) => ({
          ...(c as Record<string, unknown>),
          content_id: (c as { id: string }).id,
        })) as RecommendationRow[];
      },
      enabled: Boolean(creatorId),
    }),

  bookmarks: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['bookmarks', userId] as const,
      queryFn: async () => {
        if (!userId) return [];
        const { data, error } = await supabase
          .from('bookmarks')
          .select(
            'user_id, content_id, note, created_at, content:content_id (title, slug, type, excerpt, cover_image_url, cover_image_alt)',
          )
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return (data ?? []) as Array<
          Bookmark & {
            content: {
              title: string;
              slug: string;
              type: string;
              excerpt: string | null;
              cover_image_url: string | null;
              cover_image_alt: string | null;
            } | null;
          }
        >;
      },
      enabled: Boolean(userId),
    }),

  readingHistory: (userId: string | undefined, contentType: 'article' | 'video') =>
    queryOptions({
      queryKey: ['reading-history', userId, contentType] as const,
      queryFn: async () => {
        if (!userId) return [];
        const { data, error } = await supabase
          .from('reading_progress')
          .select(
            'content_id, scroll_percent, completed, last_read_at, content:content_id (title, slug, type)',
          )
          .eq('user_id', userId)
          .order('last_read_at', { ascending: false });
        if (error) throw error;
        return (data ?? []).filter(
          (r) => (r.content as { type?: string } | null)?.type === contentType,
        ) as Array<
          ReadingProgress & { content: { title: string; slug: string; type: string } | null }
        >;
      },
      enabled: Boolean(userId),
    }),

  // The video-native sibling of readingHistory. Own-row watch_progress rows,
  // newest first (served by watch_progress_user_recent_idx). In Increment 1
  // duration_seconds is null (start-only writes) so the % resume label is dormant
  // until Increment 2 feeds position; the row still lists as "watched".
  watchHistory: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['watch-history', userId] as const,
      queryFn: async () => {
        if (!userId) return [];
        const { data, error } = await supabase
          .from('watch_progress')
          .select(
            'content_id, position_seconds, duration_seconds, completed, last_watched_at, content:content_id (title, slug, type)',
          )
          .eq('user_id', userId)
          .order('last_watched_at', { ascending: false });
        if (error) throw error;
        return (data ?? []) as Array<
          Pick<
            WatchProgress,
            'content_id' | 'position_seconds' | 'duration_seconds' | 'completed' | 'last_watched_at'
          > & { content: { title: string; slug: string; type: string } | null }
        >;
      },
      enabled: Boolean(userId),
    }),

  // Distinct videos watched — the member identity line's "N broadcasts". A live
  // own-row count (fresher than the hourly subscriber_stats matview); the
  // watch_progress PK (user_id, content_id) makes each row a distinct video.
  watchCount: (userId: string | undefined) =>
    queryOptions({
      queryKey: ['watch-count', userId] as const,
      queryFn: async (): Promise<number> => {
        if (!userId) return 0;
        const { count, error } = await supabase
          .from('watch_progress')
          .select('content_id', { count: 'exact', head: true })
          .eq('user_id', userId);
        if (error) throw error;
        return count ?? 0;
      },
      enabled: Boolean(userId),
    }),

  progressFor: (userId: string | undefined, contentId: string | undefined) =>
    queryOptions({
      queryKey: ['reading-progress', userId, contentId] as const,
      queryFn: async (): Promise<ReadingProgress | null> => {
        if (!userId || !contentId) return null;
        const { data, error } = await supabase
          .from('reading_progress')
          .select('*')
          .eq('user_id', userId)
          .eq('content_id', contentId)
          .maybeSingle();
        if (error) throw error;
        return data as ReadingProgress | null;
      },
      enabled: Boolean(userId && contentId),
    }),

  // The video-native sibling of progressFor — the saved resume row for one video.
  // The watch route reads this to seek-to-resume on reopen (Spec 33 Increment 2).
  // Own-row RLS governs it.
  watchProgressFor: (userId: string | undefined, contentId: string | undefined) =>
    queryOptions({
      queryKey: ['watch-progress', userId, contentId] as const,
      queryFn: async (): Promise<WatchProgress | null> => {
        if (!userId || !contentId) return null;
        const { data, error } = await supabase
          .from('watch_progress')
          .select('*')
          .eq('user_id', userId)
          .eq('content_id', contentId)
          .maybeSingle();
        if (error) throw error;
        return data as WatchProgress | null;
      },
      enabled: Boolean(userId && contentId),
    }),
};

// --- mutations ---

export async function followCreator(creatorId: string, followerId: string) {
  const { error } = await supabase
    .from('follows')
    .insert({ follower_id: followerId, followed_id: creatorId });
  if (error) throw error;
}

export async function unfollowCreator(creatorId: string, followerId: string) {
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('followed_id', creatorId);
  if (error) throw error;
}

export async function addBookmark(userId: string, contentId: string) {
  const { error } = await supabase
    .from('bookmarks')
    .insert({ user_id: userId, content_id: contentId });
  if (error) throw error;
}

export async function removeBookmark(userId: string, contentId: string) {
  const { error } = await supabase
    .from('bookmarks')
    .delete()
    .eq('user_id', userId)
    .eq('content_id', contentId);
  if (error) throw error;
}

export async function updateBookmarkNote(userId: string, contentId: string, note: string) {
  const { error } = await supabase
    .from('bookmarks')
    .update({ note })
    .eq('user_id', userId)
    .eq('content_id', contentId);
  if (error) throw error;
}

export async function upsertReadingProgress(args: {
  userId: string;
  contentId: string;
  scrollPercent: number;
}) {
  const completed = args.scrollPercent >= 95;
  const { error } = await supabase.from('reading_progress').upsert(
    {
      user_id: args.userId,
      content_id: args.contentId,
      scroll_percent: Math.max(0, Math.min(100, Math.round(args.scrollPercent))),
      last_read_at: new Date().toISOString(),
      completed,
      completed_at: completed ? new Date().toISOString() : null,
    },
    { onConflict: 'user_id,content_id' },
  );
  if (error) throw error;
}

// The video-native sibling of upsertReadingProgress. Own-row RLS is the wall —
// no service-role function. Written whole now: in Increment 1 it's called
// start-only (position 0, no duration), so the completed/duration logic is
// dormant until Increment 2's Bunny timeupdate instrumentation feeds position.
// No writer change needed then.
export async function upsertWatchProgress(args: {
  userId: string;
  contentId: string;
  creatorId: string;
  positionSeconds?: number;
  durationSeconds?: number;
}) {
  const pos = Math.max(0, Math.round(args.positionSeconds ?? 0));
  const dur = args.durationSeconds != null ? Math.max(0, Math.round(args.durationSeconds)) : null;
  const completed = dur != null && dur > 0 && pos / dur >= 0.95;
  const { error } = await supabase.from('watch_progress').upsert(
    {
      user_id: args.userId,
      content_id: args.contentId,
      creator_id: args.creatorId,
      position_seconds: pos,
      duration_seconds: dur,
      completed,
      completed_at: completed ? new Date().toISOString() : null,
      last_watched_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,content_id' },
  );
  if (error) throw error;
}
